
import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnDestroy, OnInit, Output } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { FeatureKeys, FeatureService } from "company/components/features/feature.service";
import { EntityConfigUtil } from "config/utils/entity-config.util";
import { ColorPickerInputComponent } from "rl-common/components/color-picker-input/color-picker-input.component";
import { DropdownMultipleComponent } from "rl-common/components/dropdown/dropdown-multiple/dropdown-multiple.component";
import { DropdownSingleComponent } from "rl-common/components/dropdown/dropdown-single/dropdown-single.component";
import { DropdownOptions } from "rl-common/components/dropdown/dropdown.models";
import { AmortizationModel } from "rl-common/components/financial/financial.models";
import { FormPlaceholderComponent } from "rl-common/components/form-placeholder/form-placeholder.component";
import { FormTableControlDirective } from "rl-common/components/form-table/form-table-control.directive";
import { FormTableMessageDirective } from "rl-common/components/form-table/form-table-message.directive";
import { FormTableRowComponent } from "rl-common/components/form-table/form-table-row/form-table-row.component";
import { NumberInputComponent } from "rl-common/components/number-input/number-input.component";
import { TextInputComponent } from "rl-common/components/text/text-input/text-input.component";
import { ICharacteristicTemplate } from "rl-common/models/i-characteristic-template";
import { ICharacteristicTemplateGroup } from "rl-common/models/i-characteristic-template-group";
import { CharTypeId, ConstUtils, LockingLogicIndicator, TemplateGroupIDs, VisibilityIndicator } from "rl-common/rl-common.consts";
import { IWfProcesses } from "rl-common/services/company/company.models";
import { EntityConfigService } from "rl-common/services/entity-config/entity-config.service";
import { GrowlerService } from "rl-common/services/growler.service";
import { ProgressService } from "rl-common/services/progress.service";
import { PromptService } from "rl-common/services/prompt/prompt.service";
import { SessionService } from "rl-common/services/session.service";
import { DealScopedOptionsUtil } from "rl-common/utils/deal-scoped-options.util";
import { Subscription } from "rxjs";
import { take } from "rxjs/operators";

interface TemplateForm {
	templateId?: FormControl<number>;
	charTypeId: FormControl<number>;
	sequence?: FormControl<number>;
	templateName: FormControl<string>;
	systemIndicator: FormControl<number>;
	templateGroup: FormControl<ICharacteristicTemplateGroup>;
	workflowProcess: FormControl<IWfProcesses>;
	calcSequence: FormControl<number>;
	visualization: FormControl<string>;
	dealScoped: FormControl<KeyValueOption>;
	selectedAmortModels: FormControl<AmortizationModel[]>;
	lockLogicIndicator: FormControl<KeyValueOption>;
	extractableIndicator: FormControl<boolean>;
	hint: FormControl<string>;
}

interface KeyValueOption {
	key: number;
	value: string;
}
@Component({
	selector: "rl-template-edit",
	templateUrl: "./template-edit.component.html",
	styleUrls: ["./template-edit.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	imports: [ReactiveFormsModule, FormsModule, TextInputComponent, DropdownSingleComponent, DropdownMultipleComponent, NumberInputComponent, ColorPickerInputComponent, FormTableRowComponent, FormTableControlDirective, FormTableMessageDirective, FormPlaceholderComponent]
})
export class TemplateEditComponent implements OnInit, OnDestroy {

	@Input()
	_charTypeId: number;

	@Input()
	_templateId: number;

	@Input()
	_charTypeName: string;

	@Input()
	templates: ICharacteristicTemplate[];

	@Output()
	onComplete = new EventEmitter<boolean>();

	_subs: Subscription[] = [];

	title: string;
	formLoaded = false;

	groups: DropdownOptions<ICharacteristicTemplateGroup> = {
		items: [],
		rowKeyFn: (type: ICharacteristicTemplateGroup) => type.characteristicTemplateGroupID,
		rowLabelFn: (type: ICharacteristicTemplateGroup) => type.characteristicTemplateGroupName
	};
	processes: DropdownOptions<IWfProcesses> = {
		items: [],
		rowKeyFn: (type: IWfProcesses) => type.processID,
		rowLabelFn: (type: IWfProcesses) => type.processName
	};
	dealScopedOptionsList: DropdownOptions<KeyValueOption> = {
		items: [],
		rowKeyFn: (type: KeyValueOption) => type.key,
		rowLabelFn: (type: KeyValueOption) => type.value
	};
	selectedTemplate: ICharacteristicTemplate;
	form: FormGroup<TemplateForm>;
	isSaving = false;

	private _storedWfProcessVal: IWfProcesses = null;

	isSuperAdmin: boolean;

	amortOptions: DropdownOptions<AmortizationModel> = {
		rowLabelFn: (row) => row.name,
		rowKeyFn: (row) => row.id,
		items: [],
	};

	componentLockLogicOptions: DropdownOptions<KeyValueOption> = {
		items: [
			{ key: LockingLogicIndicator.Default, value: "Follow Parent Module Record's Lock Status" },
			{ key: LockingLogicIndicator.RevisionStatus, value: "Lock only when the Parent Module Record is in Revision Status 'Complete' or 'Approved'" }
		],
		rowKeyFn: (type: KeyValueOption) => type.key,
		rowLabelFn: (type: KeyValueOption) => type.value
	};


	get isDeal() {
		return this._charTypeId === CharTypeId.Deal;
	}

	get isConfigEditLocked(): boolean {
		return this.selectedTemplate?.configEditLocked === true;
	}

	straightLine = true;
	showLockingLogic = false;

	readonly isProductApprovalsEnabled = toSignal(
		this._featureService.isEnabled$(FeatureKeys.ProductApprovals),
		{ initialValue: false }
	)

	constructor(
		private readonly _fb: FormBuilder,
		private readonly _growlerService: GrowlerService,
		private readonly _entityConfigService: EntityConfigService,
		private readonly _progressService: ProgressService,
		private readonly _sessionService: SessionService,
		private readonly _featureService: FeatureService,
		private readonly _promptService: PromptService) { }

	async ngOnInit(): Promise<void> {
		this.isSuperAdmin = await this._sessionService.isRlAdmin$.pipe(take(1)).toPromise();

		if (this._templateId === null) {
			// create mode
			this.title = "Create New " + this._charTypeName + " Template";
			await this.setupCreateTemplate();
		} else {
			this.title = "Edit " + this._charTypeName + " Template";
			await this.setupEditTemplate();
		}
		this.showLockingLogic = await this.canShowLockingLogic();
	}

	async setupDropdowns() {
		if (this._charTypeId === CharTypeId.Right) {
			this.getDealScopedOptionsList();
		}

		if (this._charTypeId === CharTypeId.Deal) {
			this.amortOptions.items = await this._entityConfigService.amortizationModels(this._charTypeId).toPromise();
		}

		const allGroups = await this._entityConfigService.getTemplateGroupList(this._charTypeId).toPromise();

		const isCharTypeProjOrDoc = this._charTypeId === CharTypeId.Project || this._charTypeId === CharTypeId.Document;
		this.groups.items = (!this.isProductApprovalsEnabled() && isCharTypeProjOrDoc)
			? allGroups.filter(
				item => item.characteristicTemplateGroupID !== TemplateGroupIDs.ProductApprovalsTemplateGroupId
			)
			: allGroups;
		this.processes.items = await this._entityConfigService.getWorkflowProcesses(this._charTypeId).toPromise();
		this.showLockingLogic = await this.canShowLockingLogic();
	}

	getTemplateGroupById(templateGroupID = null): ICharacteristicTemplateGroup {
		return {
			characteristicTemplateGroupID: templateGroupID,
			characteristicTemplateGroupName: null,
			sequenceNumber: null
		};
	}

	getWorkflowProcessById(processID = null): IWfProcesses {
		return {
			processID: processID,
			charTypeID: null,
			processName: null,
			processDescription: null
		};
	}

	async setupEditTemplate() {
		await this.setupDropdowns();
		const { template, workflowProcessId, selectedAmortModels } = await this._entityConfigService.getTemplate(this._charTypeId, this._templateId).toPromise();
		const selectedModels = this.amortOptions.items.filter(x => selectedAmortModels.includes(x.id));
		this.selectedTemplate = template;
		this.form = this._fb.group<TemplateForm>({
			templateId: this._fb.control({ value: template.templateID, disabled: true }),
			charTypeId: this._fb.control({ value: this._charTypeId, disabled: true }),
			sequence: this._fb.control({ value: template.sequenceNumber, disabled: true }),
			templateName: this._fb.control(template.templateName, [Validators.required, this.duplicateTemplateNameValidator]),
			systemIndicator: this._fb.control({ value: template.systemIndicator, disabled: !this.isSuperAdmin }),
			templateGroup: this._fb.control(this.getTemplateGroupById(template.templateGroupID)),
			workflowProcess: this._fb.control(this.getWorkflowProcessById(workflowProcessId), {}),
			dealScoped: this._fb.control({ key: template.dealScoped, value: null }),
			visualization: this._fb.control(template.visualization),
			calcSequence: this._fb.control(template.calcSequence),
			selectedAmortModels: this._fb.control(selectedModels),
			extractableIndicator: this._fb.control(template.extractableIndicator == 1 ? true : false),
			hint: this._fb.control(template.hint),
			lockLogicIndicator: this._fb.control({ key: template.lockLogicIndicator, value: "" })
		});
		this.title = "Edit " + this._charTypeName + " Template - <strong>" + template.templateName + "</strong>";

		this.setupWorkflowProcessChangeConfirmation();

		if (this.isConfigEditLocked) {
			this.form.disable();
		}

		this.formLoaded = true;
	}

	async setupCreateTemplate() {
		await this.setupDropdowns();
		this.form = this._fb.group<TemplateForm>({
			charTypeId: this._fb.control({ value: this._charTypeId, disabled: true }, Validators.required),
			templateName: this._fb.control("", [Validators.required, this.duplicateTemplateNameValidator]),
			systemIndicator: this._fb.control({ value: 0, disabled: !this.isSuperAdmin }),
			templateGroup: this._fb.control(this.getTemplateGroupById(this.groups.items[0]?.characteristicTemplateGroupID), Validators.required),
			workflowProcess: this._fb.control(this.getWorkflowProcessById(this.processes.items[0]?.processID), {}),
			dealScoped: this._fb.control({ key: 0, value: null }),
			visualization: this._fb.control(""),
			calcSequence: this._fb.control(0),
			selectedAmortModels: this._fb.control([]),
			lockLogicIndicator: this._fb.control({ key: LockingLogicIndicator.Default, value: "" }),
			extractableIndicator: this._fb.control(true),
			hint: this._fb.control(null)
		});

		this.setupWorkflowProcessChangeConfirmation();
		this.formLoaded = true;
	}

	private getDealScopedOptionsList() {
		for (const d of EntityConfigUtil.getDealScopedOptions()) {
			if (d > -1) {
				this.dealScopedOptionsList.items.push({ key: d, value: DealScopedOptionsUtil.toDisplayName(d) });
			}
		}
	}

	duplicateTemplateNameValidator = (fc: FormControl<string>) => {
		if (!fc.value) {
			return null;
		}

		const isCreate = this._templateId === null;
		const cleanedTemplateName = fc.value.trim().toLowerCase();
		const isDuplicate = this.templates.some(t => t.templateName.trim().toLowerCase() === cleanedTemplateName && (isCreate || t.templateID !== this._templateId));

		return isDuplicate ? { "templateNameDuplicate": true } : null;
	};

	async submit($event: Event) {
		if (this.form.invalid) {
			$event.preventDefault();
		} else {
			this.isSaving = true;
			this._progressService.startProgress();
			let success = true;
			let errorMessage: string = null;
			const selectedAmortModels = this.form.controls.selectedAmortModels.value.map(x => x.id);
			let jobId: string = null;
			try {
				if (this._templateId != null) {
					const template: Partial<ICharacteristicTemplate> = {
						templateName: this.form.controls.templateName.value,
						templateGroupID: this.form.controls.templateGroup.value.characteristicTemplateGroupID,
						systemIndicator: this.form.controls.systemIndicator.value,
						dealScoped: this.form.controls.dealScoped?.value.key,
						visualization: this.form.controls.visualization?.value,
						calcSequence: this.form.controls.calcSequence?.value,
						sequenceNumber: this.form.controls.sequence?.value,
						visibilityIndicator: this.selectedTemplate?.visibilityIndicator ?? VisibilityIndicator.InternalExternal,
						lockLogicIndicator: this.form.controls.lockLogicIndicator?.value.key ?? LockingLogicIndicator.Default,
						extractableIndicator: this.form.controls.extractableIndicator?.value ? 1 : 0,
						hint: this.form.controls.hint?.value
					};
					const workflowProcessId = this.form.controls.workflowProcess.value.processID;
					jobId = await this._entityConfigService.updateTemplate(this._charTypeId, this._templateId, template as ICharacteristicTemplate, workflowProcessId, selectedAmortModels).toPromise();
				} else {
					const template: Partial<ICharacteristicTemplate> = {
						templateName: this.form.controls.templateName.value,
						templateGroupID: this.form.controls.templateGroup.value.characteristicTemplateGroupID,
						systemIndicator: this.form.controls.systemIndicator.value,
						dealScoped: this.form.controls.dealScoped?.value.key,
						visualization: this.form.controls.visualization?.value,
						calcSequence: this.form.controls.calcSequence?.value,
						visibilityIndicator: VisibilityIndicator.InternalExternal,
						lockLogicIndicator: this.form.controls.lockLogicIndicator?.value.key ?? LockingLogicIndicator.Default,
						extractableIndicator: this.form.controls.extractableIndicator?.value ? 1 : 0,
						hint: this.form.controls.hint?.value
					};

					const workflowProcessId = this.form.controls.workflowProcess.value.processID;
					jobId = await this._entityConfigService.createTemplate(this._charTypeId, template as ICharacteristicTemplate, workflowProcessId, selectedAmortModels).toPromise();
				}

				if (jobId) {
					await this._promptService.jobProgress(jobId, false, false, true).toPromise();
				}

			} catch (e) {
				success = false;
				errorMessage = e?.error?.message;
			} finally {
				this._progressService.endProgress();
				if (success) {
					this.onComplete.next(true);
					this._growlerService.success().growl("Your changes were saved.");
				} else {
					this._growlerService.error().growl(errorMessage || "Your changes did not save, please check the form and try again.");
				}
			}
		}
	}

	async canShowLockingLogic() {
		return ConstUtils.isComponentLevelCharType(this._charTypeId)
			&& await this._featureService.isEnabled$(FeatureKeys.UnifiedWorkflow).pipe(take(1)).toPromise()
			&& await this._featureService.isEnabled$(FeatureKeys.ConfigurableLayouts).pipe(take(1)).toPromise();
	}

	private setupWorkflowProcessChangeConfirmation() {
		this._storedWfProcessVal = this.form.controls.workflowProcess.value;

		const title = "Are you sure you want to change the Workflow Process on this Template?";
		const body = "<p>Changing the Workflow Process will cause all Records using this Template to " +
			"lose their Workflow Status. These orphaned Records will need to be mapped to a " +
			"Status in the new Workflow using the Status Update tool.</p>" +
			"<p>Do you wish to proceed? </p>";

		const sub = this.form.controls.workflowProcess.valueChanges.subscribe(async newValue => {
			const previousValue = this._storedWfProcessVal;

			const confirmed = await this._promptService.build(title, true)
				.withBody(body)
				.confirmAsync();

			if (confirmed) {
				this._storedWfProcessVal = newValue;
			} else {
				this.form.controls.workflowProcess.setValue(previousValue, { emitEvent: false });
			}
		});
		this._subs.push(sub);
	}

	ngOnDestroy(): void {
		this._subs.forEach(s => s.unsubscribe());
	}

	close() {
		this.onComplete.next(null);
	}

}
